import { unref, watchEffect } from "vue";

/**
 * @typedef {import("./useFinderFilterRegistry").FinderFilterRegistration} FinderFilterRegistration
 */

/**
 * Lays the bar's registered filters out by writing `style.order` onto each slot-assigned element —
 * the only thing the bar writes onto a child.
 *
 * `order` ranks flex items, and the row's flex items are what the slot assigns. A filter inside a
 * slotted wrapper is therefore ranked through that wrapper, and where two filters share one the
 * frontmost decides its place: the wrapper occupies a single position in the row either way.
 *
 * Active filters lead, in the order the visitor turned them on; the rest follow in document order.
 * An open filter keeps the position it already has, so the panel the visitor is reading does not
 * move out from under them, and it takes its new place when it closes.
 *
 * Positions are consecutive from zero and each is held by one filter. Equal `order` values would
 * leave the tie to document position, which is how an open filter would still be displaced.
 *
 * A filter that has just become active leads the row, so the bar scrolls back to the start to show
 * it where it landed. The scroller glides there rather than jumping. An open filter is not moved,
 * so the scroll waits with it and runs when it closes and takes its place.
 *
 * @param {import("vue").ShallowRef<Map<HTMLElement, FinderFilterRegistration>>} registry
 * @param {import("vue").Ref<HTMLElement | null | undefined>} scroller - the scrolling element.
 */
export function useFinderFilterOrder(registry, scroller) {
  /**
   * What the bar remembers between passes, all of it keyed by an element the registry still holds.
   * Each pass drops the entries whose element has left, so a departed filter neither reserves a
   * position nor answers for the element that takes its place.
   */
  const positions = new Map();
  const wasActive = new Set();
  const awaitingScroll = new Set();

  /**
   * @param {Map<HTMLElement, unknown> | Set<HTMLElement>} tracker
   * @param {Set<HTMLElement>} present
   */
  function forget(tracker, present) {
    for (const element of [...tracker.keys()]) {
      if (!present.has(element)) {
        tracker.delete(element);
      }
    }
  }

  /**
   * Active first by activation stamp, then the rest by document position.
   *
   * @param {FinderFilterRegistration} a
   * @param {FinderFilterRegistration} b
   */
  function byActivationThenDocument(a, b) {
    if (a.state.active !== b.state.active) {
      return a.state.active ? -1 : 1;
    }

    return a.state.active ? a.activatedAt - b.activatedAt : a.documentIndex - b.documentIndex;
  }

  /** The element the row lays out for a registration, which is its wrapper when it has one. */
  function laidOut(registration) {
    return registration.assignedElement ?? registration.element;
  }

  /**
   * @param {HTMLElement} element
   * @param {number} position
   */
  function place(element, position) {
    positions.set(element, position);
    element.style.order = String(position);
  }

  /**
   * Groups the registrations by the element the row lays out, keeping them in the order given.
   *
   * @param {FinderFilterRegistration[]} ordered
   * @returns {{ element: HTMLElement, registrations: FinderFilterRegistration[] }[]}
   */
  function byLaidOutElement(ordered) {
    const lanes = [];
    const byElement = new Map();

    for (const registration of ordered) {
      const element = laidOut(registration);
      let lane = byElement.get(element);

      if (!lane) {
        lane = { element, registrations: [] };
        byElement.set(element, lane);
        lanes.push(lane);
      }

      lane.registrations.push(registration);
    }

    return lanes;
  }

  watchEffect(() => {
    const ordered = [...registry.value.values()].sort(byActivationThenDocument);
    const lanes = byLaidOutElement(ordered);

    forget(positions, new Set(lanes.map((lane) => lane.element)));
    forget(wasActive, new Set(ordered.map((registration) => registration.element)));
    forget(awaitingScroll, new Set(ordered.map((registration) => registration.element)));

    const heldBy = new Map();
    const moving = [];

    for (const lane of lanes) {
      const open = lane.registrations.some((registration) => registration.state.open);
      // A row that has shrunk since the position was remembered has no such slot to hold, so the
      // hold lands on the last one — still the place the visitor sees it in.
      const held = positions.has(lane.element) ? Math.min(positions.get(lane.element), lanes.length - 1) : undefined;

      if (open && held !== undefined && !heldBy.has(held)) {
        heldBy.set(held, lane);
      } else {
        moving.push(lane);
      }
    }

    // A held lane is written too: its slot moves when the row shrinks under it, and the element
    // keeps whatever `order` it was last given until something writes the new one.
    for (const [slot, lane] of heldBy) {
      place(lane.element, slot);
    }

    let position = 0;

    for (const lane of moving) {
      while (heldBy.has(position)) {
        position += 1;
      }

      place(lane.element, position);
      position += 1;
    }

    for (const registration of ordered) {
      if (registration.state.active) {
        if (!wasActive.has(registration.element)) {
          awaitingScroll.add(registration.element);
        }

        wasActive.add(registration.element);
      } else {
        wasActive.delete(registration.element);
        awaitingScroll.delete(registration.element);
      }
    }

    const placed = new Set(moving.map((lane) => lane.element));
    const moved = ordered.filter(
      (registration) => awaitingScroll.has(registration.element) && placed.has(laidOut(registration)),
    );

    moved.forEach((registration) => awaitingScroll.delete(registration.element));

    const element = unref(scroller);

    // jsdom implements no scrolling, so specs exercise the ordering without it.
    if (moved.length > 0 && typeof element?.scrollTo === "function") {
      element.scrollTo({ left: 0 });
    }
  });
}
